import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, todayISO, fullName } from "@/lib/api";
import { Field } from "@/components/Field";

const blank = (date, patientId) => ({ patient_id: patientId || "", date: date || todayISO(), time: "09:00", duration: 45,
  location: "", reason: "Visita di controllo", notes: "", send_email: true, studio_id: "", tariff: "" });
const eur = (v) => (v ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });

export function AppointmentDialog({ open, onOpenChange, initial, defaultDate, fixedPatient, onSaved }) {
  const [form, setForm] = useState(blank());
  const [patients, setPatients] = useState([]);
  const [studios, setStudios] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? { ...blank(), ...initial, studio_id: initial.studio_id || "", tariff: initial.tariff || "", send_email: true } : { ...blank(defaultDate, fixedPatient?.id), studio_id: fixedPatient?.studio_id || "" });
    if (!fixedPatient) api.get("/patients").then((r) => setPatients(r.data));
    api.get("/studios").then((r) => setStudios(r.data));
    api.get("/settings/visit-tariffs").then((r) => setTariffs(r.data.tariffs));
  }, [open, initial, defaultDate, fixedPatient]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  const pickPatient = (e) => {
    const p = patients.find((x) => x.id === e.target.value);
    setForm({ ...form, patient_id: e.target.value, studio_id: p?.studio_id || form.studio_id, location: p?.studio_id ? "" : form.location });
  };
  const pickStudio = (e) => setForm({ ...form, studio_id: e.target.value, location: "" });

  const save = async (e) => {
    e.preventDefault();
    if (!form.patient_id) return toast.error("Seleziona un paziente");
    setSaving(true);
    try {
      const payload = { ...form, duration: Number(form.duration) || 45, studio_id: form.studio_id || null, tariff: form.tariff || null };
      const { data } = initial ? await api.put(`/appointments/${initial.id}`, payload) : await api.post("/appointments", payload);
      toast.success(initial ? "Appuntamento aggiornato" : "Appuntamento fissato", {
        description: form.send_email ? (data.email_sent ? "Email inviata al paziente" : "Email non inviata (paziente senza email o servizio non disponibile)") : "Nessuna email inviata",
      });
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Annullare l'appuntamento? Il paziente riceverà una notifica.")) return;
    try {
      await api.delete(`/appointments/${initial.id}`);
      toast.success("Appuntamento annullato");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto rounded-2xl" data-testid="appointment-dialog">
        <DialogHeader><DialogTitle>{initial ? "Modifica appuntamento" : "Fissa appuntamento"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Paziente">
            {fixedPatient ? (
              <div className="field bg-slate-50" data-testid="appointment-patient-fixed">{fullName(fixedPatient)}</div>
            ) : (
              <select className="field" value={form.patient_id} onChange={pickPatient} data-testid="appointment-patient-select" required>
                <option value="">Seleziona paziente…</option>
                {patients.map((p) => <option key={p.id} value={p.id}>{p.last_name} {p.first_name}</option>)}
              </select>
            )}
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Data"><input type="date" className="field" value={form.date} onChange={set("date")} required data-testid="appointment-date-input" /></Field>
            <Field label="Ora"><input type="time" className="field" value={form.time} onChange={set("time")} required data-testid="appointment-time-input" /></Field>
            <Field label="Durata (min)"><input type="number" min="5" className="field" value={form.duration} onChange={set("duration")} data-testid="appointment-duration-input" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Studio">
              {studios.length ? (
                <select className="field" value={form.studio_id} onChange={pickStudio} data-testid="appointment-studio-select">
                  <option value="">— Nessuno studio —</option>
                  {studios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              ) : (
                <input className="field" value={form.location} onChange={set("location")} placeholder="Aggiungi gli studi dalla pagina Studi" data-testid="appointment-location-input" />
              )}
            </Field>
            <Field label="Motivo"><input className="field" value={form.reason} onChange={set("reason")} data-testid="appointment-reason-input" /></Field>
          </div>
          <Field label="Importo da pagare (nella email con IBAN)">
            <select className="field" value={form.tariff} onChange={set("tariff")} data-testid="appointment-tariff-select">
              <option value="">Nessun pagamento in email</option>
              {tariffs.map((x) => <option key={x.key} value={x.key}>{x.label} – {eur(x.amount)}</option>)}
            </select>
          </Field>
          <Field label="Note interne (non visibili al paziente)">
            <textarea className="field min-h-[70px]" value={form.notes} onChange={set("notes")} data-testid="appointment-notes-input" />
          </Field>
          <div className="flex items-center justify-between rounded-xl bg-[#edf2ef] px-4 py-3">
            <div className="text-sm font-medium text-[#2f4f47]">Invia email al paziente</div>
            <Switch checked={form.send_email} onCheckedChange={set("send_email")} data-testid="appointment-send-email-switch" />
          </div>
          <div className="flex items-center justify-between gap-2 pt-2">
            {initial ? (
              <button type="button" onClick={remove} className="btn-ghost text-rose-600 hover:bg-rose-50" data-testid="appointment-delete-button">
                <Trash2 className="h-4 w-4" /> Annulla appuntamento
              </button>
            ) : <span />}
            <button type="submit" disabled={saving} className="btn-primary" data-testid="appointment-save-button">
              <Send className="h-4 w-4" /> {saving ? "Salvataggio…" : initial ? "Salva modifiche" : "Fissa appuntamento"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
